import { Fragment, useMemo } from "react";
import { Card, CardTitle, Button, Row, Col } from "reactstrap";
import { useNavigate } from "react-router-dom";
import subscriptionNames from "./subscriptionData";
import { getUser } from "../../service/AuthService";

// --- helpers for expiry (date-only, inclusive) ---
function todayYMD() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isExpired(expiryDate) {
  if (!expiryDate) return true; // no expiry = treat as not available
  const expYMD = String(expiryDate).slice(0, 10);
  const today = todayYMD();
  return expYMD < today;
}

const LEARNING_RESOURCE_NAME = "Learning Resources";
const MANAGE_USERS = "Manage Users";

const Cards = ({ creditsAvailable, expiryDate }) => {
  const user = getUser();
  const organization = user?.organization || "";
  const subscriptions = user?.subscriptions || "";

  const subscriptionList = subscriptions
    ? subscriptions.split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  const navigate = useNavigate();
  const isAdminOrg = (organization || "").toLowerCase() === "techcomb";

  const expired = useMemo(() => isExpired(expiryDate), [expiryDate]);
  const noCredits = !creditsAvailable || creditsAvailable <= 0;

  // Cards are disabled if credits expired OR balance is 0 / missing
  const disabled = expired || noCredits;

  // --------------------------
  // Route maps (future-proof)
  // --------------------------
  const routeByCardName = useMemo(
    () => ({
      "AI Model Building": "/ModelBuilder",
      "Product Pricing": "/VendorPriceTable",
      "Behavior Analytics": "/BehaviorAnalytics",
      [MANAGE_USERS]: "/ManageUsers",
      [LEARNING_RESOURCE_NAME]: "/LearningResources",
    }),
    []
  );

  // For now, tutorials route to the same page as the card itself.
  // Later, you can point these to dedicated tutorial pages.
  const tutorialRouteByCardName = useMemo(
    () => ({
      ...routeByCardName,
    }),
    [routeByCardName]
  );

  // --------------------------
  // Visibility & Active rules
  // --------------------------
  // Only techcomb users can SEE Manage Users (admin section).
  // Everyone else should never see it.
  const displaySubscriptions = useMemo(() => {
    const base = new Set(subscriptionNames);

    if (isAdminOrg) base.add(MANAGE_USERS);
    else base.delete(MANAGE_USERS);

    return Array.from(base);
  }, [isAdminOrg]);

  const isActive = (subName) => {
    // Techcomb users always have Learning Resources active
    if (isAdminOrg && subName === LEARNING_RESOURCE_NAME) return true;

    // Techcomb users always have Manage Users active
    if (isAdminOrg && subName === MANAGE_USERS) return true;

    // Everything else: active only if present in user's subscriptions
    return subscriptionList.includes(subName);
  };

  // Split into sections
  const learningResources = useMemo(
    () => displaySubscriptions.filter((n) => n === LEARNING_RESOURCE_NAME),
    [displaySubscriptions]
  );

  const manageUsersOnly = useMemo(
    () => displaySubscriptions.filter((n) => n === MANAGE_USERS),
    [displaySubscriptions]
  );

  const activeSubscriptionsOnly = useMemo(
    () =>
      displaySubscriptions.filter(
        (n) => n !== LEARNING_RESOURCE_NAME && n !== MANAGE_USERS
      ),
    [displaySubscriptions]
  );

  // --------------------------
  // Navigation helpers
  // --------------------------
  const goToCard = (cardName) => {
    if (disabled) return;
    if (!isActive(cardName)) return;

    const route = routeByCardName[cardName];
    if (route) navigate(route);
  };

  const goToTutorial = (cardName) => {
    if (disabled) return;
    if (!isActive(cardName)) return;

    const route = tutorialRouteByCardName[cardName];
    if (route) navigate(route);
  };

  const subscribeHandler = () => navigate("/Contactus");

  return (
    <div>
      {/* Active Subscriptions */}
      <Row className="mb-2">
        <Col xs="12">
          <h5 className="font-md-lg font-bold text-center">
            Your Active Subscriptions
          </h5>
        </Col>
      </Row>

      <Row className="g-4">
        {activeSubscriptionsOnly.map((cardName) => {
          const active = isActive(cardName);

          return (
            <Fragment key={cardName}>
              <Col md="6" lg="3">
                <Card
                  body
                  color={active ? "light-success" : "light-danger"}
                  style={{
                    cursor: active && !disabled ? "pointer" : "not-allowed",
                    opacity: disabled ? 0.5 : 1,
                  }}
                  onClick={() => goToCard(cardName)}
                >
                  <CardTitle tag="h5">{cardName}</CardTitle>

                  <div>
                    {active ? (
                      <Button
                        disabled={disabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          goToTutorial(cardName); 
                        }}
                      >
                        {disabled ? "Not Available" : "View Tutorial"}
                      </Button>
                    ) : (
                      <Button
                        disabled={disabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!disabled) subscribeHandler();
                        }}
                      >
                        {disabled ? "Not Available" : "Subscribe Now"}
                      </Button>
                    )}
                  </div>
                </Card>
              </Col>
            </Fragment>
          );
        })}
      </Row>

      <Row className="mt-5 mb-3">
        <Col xs="12">
          <hr style={{ opacity: 0.4 }} />
        </Col>
      </Row>

      {/* Learning Resources  */}
      <Row className="mb-2">
        <Col xs="12">
          <h5 className="font-md-lg font-bold text-center">
            Learning Resources
          </h5>
        </Col>
      </Row>

      <Row className="g-4">
        {learningResources.map((cardName) => {
          const active = isActive(cardName);

          return (
            <Fragment key={cardName}>
              <Col md="6" lg="3">
                <Card
                  body
                  color={active ? "light-info" : "light-danger"}
                  style={{
                    cursor: active && !disabled ? "pointer" : "not-allowed",
                    opacity: disabled ? 0.5 : 1,
                  }}
                  onClick={() => goToCard(cardName)}
                >
                  <CardTitle tag="h5">{cardName}</CardTitle>

                  <Button
                    disabled={disabled}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (active) goToTutorial(cardName);
                      else if (!disabled) subscribeHandler();
                    }}
                  >
                    {disabled ? "Not Available" : active ? "Open Resource" : "Subscribe Now"}
                  </Button>
                </Card>
              </Col>
            </Fragment>
          );
        })}
      </Row>

      {/* Admin Section -- Only Techcomb users)  */}
      {isAdminOrg && manageUsersOnly.length > 0 && (
        <>
          <Row className="mt-5 mb-3">
            <Col xs="12">
              <hr style={{ opacity: 0.4 }} />
            </Col>
          </Row>

          <Row className="mb-2">
            <Col xs="12">
              <h5 className="font-md-lg font-bold text-center">Admin</h5>
            </Col>
          </Row>

          <Row className="g-4">
            {manageUsersOnly.map((cardName) => {
              const active = isActive(cardName); // will be true for admin org

              return (
                <Fragment key={cardName}>
                  <Col md="6" lg="3">
                    <Card
                      body
                      color={active ? "light-success" : "light-danger"}
                      style={{
                        cursor: active && !disabled ? "pointer" : "not-allowed",
                        opacity: disabled ? 0.5 : 1,
                      }}
                      onClick={() => goToCard(cardName)}
                    >
                      <CardTitle tag="h5">{cardName}</CardTitle>

                      <Button
                        disabled={disabled}
                        onClick={(e) => {
                          e.stopPropagation();
                          // For now, tutorial routes to the same page as card
                          goToTutorial(cardName);
                        }}
                      >
                        {disabled ? "Not Available" : "Open"}
                      </Button>
                    </Card>
                  </Col>
                </Fragment>
              );
            })}
          </Row>
        </>
      )}
    </div>
  );
};

export default Cards;
